/**
 * The broker's net worth over the round, and the shape of the line that draws it.
 *
 * Net worth is cash plus what the warehouse and the convoys are marked at plus
 * what the nations still owe — `computeScore` in the core, arriving on every
 * patch as `score.netWorth`. The panel already showed all four of those numbers
 * side by side and none of them answered "am I ahead of where I was", which is
 * the only question a broker actually has.
 *
 * Kept apart from the component because the two things worth getting right here
 * are arithmetic, not markup: what gets sampled, and where a default shows up.
 */

/**
 * Ticks between samples. The clock is 5 Hz, so this is a point every three
 * seconds — fine enough
 * that a default is a visible step rather than a corner, coarse enough that the
 * line is not a thousand-point hairball.
 *
 * Sampling on TICKS rather than on arrivals is what makes the line the same
 * shape on a slow connection as on a fast one: patches arrive when they arrive,
 * but a tick is a tick.
 */
export const SAMPLE_INTERVAL_TICKS = 15;

/** A rolling window that covers the full seven-minute simulated round. */
export const MAX_SAMPLES = 256;

export interface WealthSample {
  tick: number;
  netWorth: number;
  /** Cumulative bad debt as of this sample. A RISE is somebody defaulting. */
  badDebt: number;
}

/**
 * Adds a reading to the history.
 *
 * Two behaviours in one function because they are the same rule: a reading in a
 * bucket that is already occupied REPLACES it, and a reading in a new bucket is
 * appended. That is what makes the right-hand end of the line live — it keeps
 * moving with every patch between sample boundaries — while the rest of the line
 * stays on a fixed three-second grid.
 *
 * A tick that has gone backwards is a new run on the same store, so the history
 * starts over rather than drawing a line back to the beginning of time.
 */
export function recordWealth(
  history: readonly WealthSample[],
  sample: WealthSample,
): WealthSample[] {
  const last = history[history.length - 1];
  if (last && sample.tick < last.tick) return [sample];
  if (!last) return [sample];

  const bucket = Math.floor(sample.tick / SAMPLE_INTERVAL_TICKS);
  const lastBucket = Math.floor(last.tick / SAMPLE_INTERVAL_TICKS);
  if (bucket === lastBucket) {
    const next = history.slice(0, -1);
    next.push(sample);
    return next;
  }

  const grown = history.concat(sample);
  return grown.length > MAX_SAMPLES ? grown.slice(grown.length - MAX_SAMPLES) : grown;
}

export interface Point {
  x: number;
  y: number;
}

export interface WealthGeometry {
  /** Polyline points for the net-worth line, in the given box. */
  line: Point[];
  /** The same, closed along the bottom, for the fill under it. */
  area: Point[];
  low: number;
  high: number;
  /** Where the line was when a counterparty defaulted, for the marks. */
  defaults: Point[];
  /** Net worth now, and against where the round opened. */
  latest: number;
  opening: number;
}

/**
 * The line, scaled into a box.
 *
 * The vertical scale is the range actually travelled rather than zero-based. A
 * broker who opens on 3,000,000 and ends on 3,400,000 has moved 13% and would
 * be a flat line against a zero baseline — the whole point of the chart is the
 * shape of the change, and the figures beside it carry the absolute value.
 *
 * A flat history is drawn down the middle instead of dividing by a zero range.
 */
export function wealthGeometry(
  history: readonly WealthSample[],
  width: number,
  height: number,
): WealthGeometry | null {
  if (history.length === 0) return null;

  const values = history.map((s) => s.netWorth);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = high - low;

  const xAt = (index: number): number =>
    history.length === 1 ? width : (index / (history.length - 1)) * width;
  // SVG y grows downward, so the high water mark is at the top.
  const yAt = (value: number): number =>
    span === 0 ? height / 2 : height - ((value - low) / span) * height;

  const line = history.map((sample, index) => ({ x: xAt(index), y: yAt(sample.netWorth) }));

  const area = line.concat([
    { x: line[line.length - 1].x, y: height },
    { x: line[0].x, y: height },
  ]);

  // A default is a rise in cumulative bad debt between two samples: a nation was
  // eliminated and what it owed stopped being worth anything. It is marked
  // rather than left to be inferred, because the drop it causes looks exactly
  // like a drop from spending.
  const defaults: Point[] = [];
  for (let i = 1; i < history.length; i += 1) {
    if (history[i].badDebt > history[i - 1].badDebt + 1e-9) defaults.push(line[i]);
  }

  return {
    line,
    area,
    low,
    high,
    defaults,
    latest: values[values.length - 1],
    opening: values[0],
  };
}

/** `M x y L x y ...` for a point list. Rounded: sub-pixel precision is noise. */
export function pathOf(points: readonly Point[]): string {
  if (points.length === 0) return "";
  const round = (n: number): string => (Math.round(n * 10) / 10).toString();
  return points
    .map((p, i) => `${i === 0 ? "M" : "L"}${round(p.x)} ${round(p.y)}`)
    .join(" ");
}
